import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { AppShell, Button, ThemeToggle, type AppShellNavItem } from '@kmgestao/ui';

import { Brand } from '../../components/Brand.js';
import { useSignOut } from '../../features/auth/index.js';

export interface ClinicShellProps {
  children: ReactNode;
}

/**
 * Shell das rotas do admin da clínica (contraparte do SuperadminShell).
 * Mantém o mesmo `AppShell` do design system — o que muda são os itens
 * de navegação. Rotas de visão geral (dashboard, pacientes, agenda, etc.)
 * chegam nos PRs de M1+; por ora só "Configurações › IA" está ativo.
 */
export function ClinicShell({ children }: ClinicShellProps) {
  const { t } = useT('ai-clinic');
  const { t: tCommon } = useT('common');
  const location = useLocation();
  const signOut = useSignOut();

  const navItems: AppShellNavItem[] = [
    {
      id: 'dashboard',
      label: t('nav.overview'),
      href: '/dashboard',
      active: location.pathname === '/dashboard',
    },
    {
      id: 'profile',
      label: t('nav.profile'),
      href: '/perfil',
      active: location.pathname.startsWith('/perfil'),
    },
    {
      id: 'team',
      label: t('nav.team'),
      href: '/equipe',
      active: location.pathname.startsWith('/equipe'),
    },
    {
      id: 'appearance',
      label: t('nav.appearance'),
      href: '/identidade-visual',
      active: location.pathname.startsWith('/identidade-visual'),
    },
    {
      id: 'plan',
      label: t('nav.plan'),
      href: '/plano',
      active: location.pathname.startsWith('/plano'),
    },
    {
      id: 'ai-settings',
      label: t('nav.aiSettings'),
      href: '/settings/ai',
      active: location.pathname.startsWith('/settings/ai'),
    },
  ];

  return (
    <AppShell
      brand={<Brand />}
      brandSidebar={<Brand onDark />}
      navItems={navItems}
      topbarRight={
        <>
          <ThemeToggle />
          <Button
            size="sm"
            variant="outline"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
          >
            {signOut.isPending ? tCommon('status.loading') : 'Sair'}
          </Button>
        </>
      }
      sidebarFooter={<p className="text-xs text-sidebar-foreground/60">v0.0.0 · dev build</p>}
    >
      {children}
    </AppShell>
  );
}
